import withRole from "../../Hooks/withRole";
import {
  EComponentTypes,
  ETableEditAction,
  OrderStatuses,
} from "../../Utils/Enums";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import genericStyles from "../../Styles/genericStyles.module.css";
import { ReportsTypes, ReportTitleMap } from "../../Utils/Constants";
import {
  instructorClientReportFilters,
  instructorClientsColumns,
} from "./ReportConfigs/instructorClientsReport";
import { useEffect, useMemo, useState } from "react";
import {
  orderStatusColumns,
  orderStatusFilters,
} from "./ReportConfigs/orderStatusesReport";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { getData } from "../../Services/getData";
import { AxiosResponse } from "axios";
import Filters from "../Widgets/Filters";
import { Filter, GroupedStatus } from "../../Utils/Types";
import { Vocabulary } from "../../Utils/Vocabulary";
import Config from "../../Utils/Config";
import * as yup from "yup";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { getFromCacheOrFetch } from "../../Utils/getFromCacheOrFetch";
import { useLocation, useParams } from "react-router-dom";
import { workPointsColumns } from "./ReportConfigs/workPointsReport";
import { isDefined, OrderStatusType } from "../../Utils/Utils";
import { clientsColumns, clientsFilters } from "./ReportConfigs/clientsReport";
import {
  InstructorModel,
  OrderStatusModel,
  ProductsModel,
  UserModel,
} from "../../Utils/Models";
import {
  instructorsTimesheetFilters,
  instructorTimesheetColumns,
} from "./ReportConfigs/instructorTimesheetReport";
import { companyReportColumn } from "./ReportConfigs/companyReport";
import {
  userActionsColumns,
  userActionsFilters,
} from "./ReportConfigs/userActionsReport";
import {
  userSalaryColumn,
  userSalaryFilters,
} from "./ReportConfigs/userSalaryReport";
import { TableCell, TableRow, Typography } from "@mui/material";
import {
  drivingHoursColumns,
  drivingHoursFilters,
} from "./ReportConfigs/drivingHoursReport";
import { clientsInstructorColumns } from "./ReportConfigs/clientsInstructorReport";
import {
  clientPaymentsColumns,
  clientPaymentsFilters,
} from "./ReportConfigs/clientPaymentsReport";
import {
  passRateColumns,
  passRateExpandableColumns,
  passRateFilters,
  passRateStudentColumns,
} from "./ReportConfigs/passRateReport";

/**
 * The component that renders all the reports.
 * The component fetches the list of users, work points, companies, instructors and office assistants
 * from the server and stores them in the state.
 * It also fetches the list of order statuses from the server and stores them in the state.
 * The component renders a table for each report, with columns and filters based on the report type.
 * The filters are rendered in a separate component.
 * The component also renders the table with the data based on the report type.
 */
function ReportsTable() {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const { type } = useParams();
  const orderStatusType =
    urlParams.get("orderStatusType") ?? OrderStatusType.License;
  const [users, setUsers] = useState<UserModel[]>([]);
  const memo = useCustomMemo();
  const { setStorageData } = memo;
  const orderStatuses = memo.storage.orderStatuses;
  const workPoints = memo.storage.workPoints;
  const companies = memo.storage.companies;
  const instructorsAsUsers = memo.storage.instructors;
  const officeAssistants = memo.storage.officeAssistants;
  const [instructors, setInstructors] = useState([] as InstructorModel[]);
  const [products, setProducts] = useState([] as ProductsModel[]);

  /**
   * Fetches the list of all instructors from the server.
   * The data is retrieved from the instructors endpoint and stored in the state.
   * If the response contains no data, the function exits early.
   */
  const getInstructors = (workPointId?: string | null) => {
    const workPointParam = workPointId ? `&workPointId=${workPointId}` : "";
    const url = `${endpoints.instructor.user}/paginated?page=0&perPage=100000${workPointParam}`;
    getData(url).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setInstructors(res.data?.results);
    });
  };

  /**
   * Fetches the list of all users from the server.
   * The data is retrieved from the main user endpoint and stored in the state.
   * If the response contains no data, the function exits early.
   */

  const getUsers = () => {
    getFromCacheOrFetch({
      cacheKey: "users",
      storage: memo.storage,
      setStorage: setStorageData,
      fetcher: () =>
        getData(`${endpoints.user.main}`).then(
          (res: AxiosResponse) => res?.data,
        ),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (data) setUsers(data as UserModel[]);
    });
  };

  /**
   * Fetches the list of all products from the server.
   * The data is retrieved from the products endpoint and stored in the state.
   * If the response contains no data, the function exits early.
   */
  const getProducts = () => {
    getFromCacheOrFetch({
      cacheKey: "products",
      storage: memo.storage,
      setStorage: setStorageData,
      fetcher: () =>
        getData(
          `${endpoints.product.main}/paginated?page=0&perPage=100000`,
        ).then((res: AxiosResponse) => res?.data?.results),
    }).then((data) => {
      if (data) setProducts(data as ProductsModel[]);
    });
  };

  /**
   * Get the list of instructors (re-fetch when workPointId filter changes for instructor clients report)
   */
  useEffect(() => {
    const workPointId =
      type === ReportsTypes.INSTRUCTOR_CLIENTS
        ? urlParams.get("workPointId")
        : null;
    getInstructors(workPointId);
  }, [urlParams.get("workPointId")]);

  useEffect(() => {
    getUsers();
    getProducts();
  }, []);

  /**
   * Given an array of OrderStatusModel, returns an object with three properties
   * each containing an array of strings. The three properties are Finalizat,
   * FinalizatNeconform and Nefinalizat.
   * @param statusArray an array of OrderStatusModel
   * @returns an object with 3 properties, each containing an array of strings
   */
  const groupStatuses = (statusArray: OrderStatusModel[]) => {
    const result: GroupedStatus[] = [
      { name: OrderStatuses.FINALIZAT, _id: [] },
      { name: OrderStatuses.FINALIZAT_NECONFORM, _id: [] },
      { name: OrderStatuses.NEFINALIZAT, _id: [] },
    ];

    statusArray.forEach((status) => {
      if (status.name === OrderStatuses.FINALIZAT) {
        result[0]._id.push(status._id ?? "");
      } else if (status.name === OrderStatuses.FINALIZAT_NECONFORM) {
        result[1]._id.push(status._id ?? "");
      } else {
        result[2]._id.push(status._id ?? "");
      }
    });

    return result;
  };

  /**
   * based on type, show different columns
   * */
  const {
    columns,
    additionalFilters,
    expandableRows,
    expandableRowsHeader,
    expandableRowObjectLocationName,
    expandableColumns,
    expandableSubRowsKey,
    expandableSubColumns,
  } = useMemo(() => {
    const statuses = orderStatuses.filter(
      (status: any) => status?.typeId?.name === orderStatusType,
    );

    // based on type, show different columns and filters
    switch (type) {
      case ReportsTypes.INSTRUCTOR_CLIENTS: {
        const search = urlParams.get("search")?.toLowerCase() ?? "";
        const matched = search
          ? instructors.filter((i: any) =>
              `${i.firstName} ${i.lastName}`.toLowerCase().includes(search),
            )
          : [];
        const filteredInstructors = matched.length > 0 ? matched : instructors;
        return {
          columns: instructorClientsColumns(filteredInstructors),
          additionalFilters: instructorClientReportFilters(workPoints),
        };
      }
      case ReportsTypes.ORDER_STATUSES:
        return {
          columns: orderStatusColumns(statuses),
          additionalFilters: orderStatusFilters(workPoints),
        };
      case ReportsTypes.WORK_POINTS:
        return {
          columns: workPointsColumns(workPoints),
        };
      case ReportsTypes.COMPANY: {
        const search = urlParams.get("search")?.toLowerCase() ?? "";
        const matched = search
          ? companies.filter((c: any) => c.name?.toLowerCase().includes(search))
          : [];
        const filteredCompanies = matched.length > 0 ? matched : companies;
        return {
          columns: companyReportColumn(filteredCompanies),
        };
      }
      case ReportsTypes.CLIENTS:
        return {
          columns: clientsColumns,
          additionalFilters: clientsFilters(groupStatuses(orderStatuses)),
        };
      case ReportsTypes.USER_ACTIONS:
        if (users.length)
          return {
            columns: userActionsColumns,
            additionalFilters: userActionsFilters(users),
          };
        return { columns: [] };
      case ReportsTypes.INSTRUCTOR_TIMESHEET:
        return {
          columns: instructorTimesheetColumns,
          additionalFilters: instructorsTimesheetFilters(instructors),
        };
      case ReportsTypes.USER_SALARY:
        return {
          columns: userSalaryColumn,
          additionalFilters: userSalaryFilters(
            officeAssistants,
            instructorsAsUsers,
          ),
        };
      case ReportsTypes.DRIVING_HOURS:
        return {
          columns: drivingHoursColumns,
          additionalFilters: drivingHoursFilters(instructors),
        };
      case ReportsTypes.CLIENTS_INSTRUCTOR:
        return {
          columns: clientsInstructorColumns,
          additionalFilters: instructorsTimesheetFilters(instructors),
        };
      case ReportsTypes.CLIENT_PAYMENTS:
        return {
          columns: clientPaymentsColumns,
          additionalFilters: clientPaymentsFilters(instructors, products),
        };
      case ReportsTypes.PASS_RATE:
        return {
          columns: passRateColumns(workPoints),
          additionalFilters: passRateFilters(products, instructors, workPoints),
          expandableRows: true,
          expandableRowsHeader: true,
          expandableRowObjectLocationName: "products",
          expandableColumns: passRateExpandableColumns,
          expandableSubRowsKey: "students",
          expandableSubColumns: passRateStudentColumns,
        };
      default:
        return { columns: [] };
    }
  }, [urlParams]);

  /**
   * Filters
   */
  const filters: Filter[] = [
    {
      property: "start",
      label: Vocabulary.startDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultStartDate,
    },
    {
      property: "end",
      label: Vocabulary.endDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultEndDate,
    },
    ...(additionalFilters ?? []),
  ];

  /**
   * Returns a JSX element representing the custom footer row.
   * @param {any} footerFields - object containing the totalAmount
   * @returns {JSX.Element} - a JSX element representing the custom footer row
   */
  const renderCustomFooter = (footerFields: any) => {
    if (!isDefined(footerFields)) return;

    /**
     * Returns a JSX element representing the custom footer row.
     */
    return (
      <>
        <TableRow>
          <TableCell colSpan={1} />
          <TableCell colSpan={11}>
            <Typography>
              <strong
                style={{
                  color: footerFields?.totalIncome ? "#2e7d32" : "#d32f2f",
                }}
              >
                {footerFields?.totalIncome
                  ? `${Vocabulary.totalIncomeFromUser}: ${footerFields.totalIncome}`
                  : `${Vocabulary.totalPaidToUser}: ${footerFields.totalAmount}` +
                    `${Vocabulary.currency}`}
              </strong>
            </Typography>
          </TableCell>
        </TableRow>
      </>
    );
  };

  /**
   * Renders the table
   *
   * Some report types have a filter default derived from async-loaded data
   * (e.g. instructorId defaults to the first fetched instructor). Rendering
   * before that data arrives would fetch once unfiltered and once filtered,
   * flashing unfiltered results first - so each such type waits here for its
   * data instead.
   */
  const isMissingDataRequiredForDefaultFilters: Partial<Record<string, boolean>> = {
    [ReportsTypes.USER_ACTIONS]: users.length === 0,
    [ReportsTypes.INSTRUCTOR_TIMESHEET]: instructors.length === 0,
    [ReportsTypes.CLIENTS_INSTRUCTOR]: instructors.length === 0,
  };
  if (type && isMissingDataRequiredForDefaultFilters[type]) return;
  return (
    <div className={genericStyles.mainContainer}>
      {<Filters key={type} filters={filters} defaultOpen />}
      <MUIDataTableCustomStyle
        key={`table-${type}`}
        title={ReportTitleMap[type ?? ""] ?? ""}
        CRUDEndPoint={`${endpoints.reports}/${type}`}
        columns={columns}
        onEditAction={ETableEditAction.NONE}
        hideAddButton
        hideDeleteButton
        hideEditButton
        customFooter={renderCustomFooter}
        onRowClick={(rowData: any) => {
          if (rowData?.orderId)
            window.open(`${localUrlEnum.order}/${rowData.orderId}`, "_blank");
        }}
        showPrintButton
        showDownloadButton
        exportWithTimestamp
        hideSearch={type === ReportsTypes.PASS_RATE}
        expandableRows={expandableRows}
        expandableRowsHeader={expandableRowsHeader}
        expandableRowObjectLocationName={expandableRowObjectLocationName}
        expandableColumns={expandableColumns}
        expandableSubRowsKey={expandableSubRowsKey}
        expandableSubColumns={expandableSubColumns}
        onExpandableSubRowClick={(student: any) => {
          if (student?.orderId)
            window.open(`${localUrlEnum.order}/${student.orderId}`, "_blank");
        }}
      />
    </div>
  );
}

export default withRole(ReportsTable);
